import moment from 'moment';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import * as yup from 'yup';

import { useAuth } from '@/api';
import { useGetOtp } from '@/api/auth/getOtp';
import { useVerifyOtp } from '@/api/auth/verifyOtp';
import { useFeatureFlags } from '@/api/feature-flags';
import CloseIcon from '@/assets/images/icons/close-icon.svg';
import { useNotificationStore } from '@/common';
import { Home } from '@/common/components';
import UserDetails from '@/common/components/Auth/UserDetails';
import { FieldWrapper, Form } from '@/common/components/Form';
import { OtpInput } from '@/common/components/Form/input/OTPInput';
import ButtonLoader from '@/common/components/UiLoader/ButtonLoader';
import useMoengageStore from '@/store/moengage';
import type {
  GetOtpArgsType,
  VerifyOtpArgsType,
  VerifyOtpResponse,
} from '@/types/auth';
import { AuthSEO } from '@/utils/constants/MetaData';
import { createMoengageEvent } from '@/utils/moengage';

const phoneRegExp = /^[6-9][0-9]{9}$/;

type MobileNumberType = {
  mobileNumber: string;
};

const mobileNumberSchema = yup.object({
  mobileNumber: yup
    .string()
    .matches(phoneRegExp, 'Enter valid mobile number')
    .required('Mobile number is required'),
});
const Auth = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(0);
  const [showOtpError, setShowOtpError] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const router = useRouter();
  const getOtp = useGetOtp();
  const verifyOtp = useVerifyOtp();
  const closeModalRef = useRef<HTMLButtonElement>(null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const openModalRef = useRef<HTMLButtonElement>(null);
  const [consentAccepted, setConsentAccepted] = useState<boolean>(false);
  const { addNotification } = useNotificationStore();
  const { userDetails } = useAuth();
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [GTMDataLayer, setGTMDataLayer] = useState(false);
  const { featureFlags, isFlagsFetched } = useFeatureFlags();

  const navigateToNext = () => {
    const { params } = router.query;
    if (isFlagsFetched && featureFlags.maintenance_mode) {
      window.location.href = '/maintenance';
      return;
    }
    if (Array.isArray(params)) {
      // const url = params.join('/');
      // console.log({ query }, typeof (query))
      // const validatedQuery: QueryParams = {};
      // for (const key in query) {
      //   if (query.hasOwnProperty(key) && typeof query[key] === 'string') {
      //     validatedQuery[key] = query[key];
      //   }
      // }
      // const searchParams = new URLSearchParams(validatedQuery).toString();
      // const fullUrl = `${url}${searchParams ? `?${searchParams}` : ''}`;
      // console.log({ fullUrl });
      // // window.location.href = fullUrl;
      let fullPath = router.asPath;
      fullPath = fullPath.replace('/auth', '');
      window.location.href = fullPath;
      return;
    }
    router.push('/gold-loans');
  };
  useEffect(() => {
    if (isFlagsFetched && featureFlags.maintenance_mode) {
      window.location.href = '/maintenance';
    }
  }, []);
  useEffect(() => {
    if (showSignupModal) {
      openModalRef.current?.click();
    } else {
      setShowSignupModal(true);
    }
  }, [showSignupModal]);

  useEffect(() => {
    setOtp(new Array(6).fill(''));
  }, [verifyOtp.isError]);

  useEffect(() => {
    let interval: any;
    if (activeStep === 2) {
      interval = setInterval(() => {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        }
        if (seconds === 0) {
          if (minutes === 0) {
            clearInterval(interval);
          } else {
            setSeconds(59);
            setMinutes(minutes - 1);
          }
        }
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [minutes, seconds, activeStep]);

  const closeModal = () => {
    closeModalRef.current?.click();
    setActiveStep(1);
    setMobileNumber('');
    setOtp(new Array(6).fill(''));
  };

  const tagManagerArgs = {
    dataLayer: {
      event: 'linkClick',
      fullName: `${userDetails?.firstName || ''} ${
        userDetails?.lastName || ''
      }`,
      userMobileNumber: userDetails?.mobilePhone || '',
      emailAddress: userDetails?.emailAddress || '',
      userType: isFirstLogin ? 'NEW_USER' : 'EXISTING_USER',
      userProject: 'MFLONE',
      page: 'Home',
    },
    dataLayerName: 'MFOneWebUserDetails',
  };
  useEffect(() => {
    if (!userDetails) TagManager.dataLayer(tagManagerArgs);
  }, [GTMDataLayer, userDetails]);

  const validateOtp = async () => {
    const verifyOtpRequestBody: VerifyOtpArgsType = {
      phone: mobileNumber,
      otpResponse: {
        otp: otp.join(''),
        sessionId,
      },
      consentAccepted,
    };
    const res: VerifyOtpResponse = await verifyOtp.mutateAsync(
      verifyOtpRequestBody
    );
    // @ts-ignore
    Moengage.add_unique_user_id(mobileNumber);
    // TODO : Setting value whether firstlogin or not
    setIsFirstLogin(res.data.isFirstLogin);
    // TODO : Adding GTM on verifying the OTP
    if (res.status === 200) {
      // Send the linkClick event to GTM
      setGTMDataLayer(true);
    }

    if (!res.data.isFirstLogin) {
      closeModal();
      navigateToNext();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const resetTimer = () => {
    setMinutes(1);
    setSeconds(0);
  };

  const resendOtp = async () => {
    setOtp(new Array(6).fill(''));
    resetTimer();
    setShowOtpError(false);
    const getOtpRequestBody: GetOtpArgsType = {
      recipientMobileNumber: mobileNumber,
    };
    const res = await getOtp.mutateAsync(getOtpRequestBody);
    setSessionId(res?.data?.data.sessionId);
  };

  const [loginSignupVerifyClicked, loginSignupContinueClicked] =
    useMoengageStore((state) => [
      state.loginSignupVerifyClicked,
      state.loginSignupContinueClicked,
    ]);

  // TODO : Adding GTM on verifying the OTP
  const handleOtpSubmit = () => {
    createMoengageEvent(loginSignupVerifyClicked, {
      created_date: moment().format('YYYY-MM-DD HH:mm:ss'),
      mobile_number: mobileNumber,
    });
    const isOtpFilled = otp.every((value) => value !== '');
    if (isOtpFilled) {
      setShowOtpError(false);
      validateOtp();
    } else {
      setShowOtpError(true);
    }
  };

  const clearOtpInput = (e: any) => {
    if (e.target.id === 'signupModal') {
      setOtp(new Array(6).fill(''));
    }
  };

  return (
    <>
      <Head>
        <title>{AuthSEO.metaTitle}</title>
        <meta name="description" content={AuthSEO.metaDescription} />
        <meta property="og:description" content={AuthSEO.metaOGDescription} />
        <link rel="canonical" href="https://muthootfincorpone.com/auth/" />
      </Head>
      <main>
        <Home />
        <button
          data-toggle="modal"
          data-target="#signupModal"
          ref={openModalRef}
          // style={{ display: 'none' }}
          className="d-none"
        ></button>
        <div
          className="modal loanDetails__modal repayModal visitSchedule login pledge loginFirstModal otpModal"
          id="signupModal"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="signupModalLabel"
          aria-hidden="true"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          onClick={clearOtpInput}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-static"
            role="document"
          >
            <div className="modal-content">
              <div className="modal__title justify-content-end">
                <button
                  data-dismiss="modal"
                  ref={closeModalRef}
                  onClick={closeModal}
                >
                  <Image
                    style={{ color: undefined }}
                    className="img-fluid"
                    src={CloseIcon}
                    alt="close icon"
                  />
                </button>
              </div>
              {activeStep === 1 && (
                <Form<MobileNumberType, typeof mobileNumberSchema>
                  onSubmit={async (values) => {
                    setMobileNumber(values.mobileNumber);
                    createMoengageEvent(loginSignupContinueClicked, {
                      created_date: moment().format('YYYY-MM-DD HH:mm:ss'),
                      mobile_number: values.mobileNumber,
                    });
                    if (consentAccepted) {
                      const getOtpRequestBody: GetOtpArgsType = {
                        recipientMobileNumber: values.mobileNumber,
                      };
                      const { data } = await getOtp.mutateAsync(
                        getOtpRequestBody
                      );
                      setActiveStep(activeStep + 1);
                      setSessionId(data.data.sessionId);
                    } else {
                      addNotification({
                        type: 'error',
                        title: 'Consent Required',
                        message: 'Please share you consent, thank you',
                      });
                    }
                  }}
                  schema={mobileNumberSchema}
                  options={{
                    defaultValues: { mobileNumber },
                  }}
                >
                  {({ register, formState }) => (
                    <div className="stepper--content active">
                      <p className="login__title text--darkBlue text--bold text-center">
                        Sign Up/Login
                      </p>
                      <p className="text--regular text--gray login__desc text-center">
                        Please enter your mobile number to verify your identity
                      </p>
                      <div className="input-container">
                        <FieldWrapper
                          label={''}
                          error={formState.errors.mobileNumber}
                        >
                          <div className="input-form phone">
                            <span className="text--regular text--black">
                              +91
                            </span>
                            <input
                              type="text"
                              placeholder="Enter 10-digit phone number"
                              {...register('mobileNumber')}
                              data-hj-allow
                            />
                          </div>
                        </FieldWrapper>
                      </div>
                      <p className="login__terms text--gray text--regular text-center">
                        <input
                          type="checkbox"
                          className="om-auth-consent-input"
                          checked={consentAccepted}
                          onChange={() => setConsentAccepted(!consentAccepted)}
                        />
                        By continuing, you agree to Muthoot FinCorp ONEs{' '}
                        {/* ONE Muthoots */}
                        <Link
                          href="/terms-and-conditions"
                          className="text--darkBlue text--semi"
                          data-dismiss="modal"
                          onClick={(e) => {
                            e.preventDefault();
                            router.push('/terms-and-conditions');
                          }}
                          id="auth_terms-use"
                        >
                          Terms of Use
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="/privacy-policy"
                          className="text--darkBlue text--semi"
                          data-dismiss="modal"
                          onClick={(e) => {
                            e.preventDefault();
                            router.push('/privacy-policy');
                          }}
                          id="auth_privacy-policy"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </p>
                      <div className="action-container">
                        <button
                          type="submit"
                          id="login-continue"
                          className={`custom__button custom__button--dark text--bold w-100 stepperNext position-relative ${
                            getOtp.isLoading ? 'disabled' : ''
                          }`}
                        >
                          Continue
                          {getOtp.isLoading && <ButtonLoader />}
                        </button>
                      </div>
                      <p className="text--regular text--gray login__existing text-center">
                        <span
                          // href="#"
                          className="text--regular  openThirdModalLogin" // text--darkBlue
                        >
                          Contact us{' '}
                        </span>
                        For any assistance
                      </p>
                    </div>
                  )}
                </Form>
              )}
              {activeStep === 2 && (
                <form
                  className={`stepper--content otpContent active`}
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  <p className="login__title text--black text--bold text-center">
                    Enter OTP
                  </p>
                  <p className="text--regular text--gray login__desc text-center">
                    Please enter the OTP sent to <br /> {mobileNumber}.{' '}
                    <button
                      type="button"
                      className="text--darkBlue text--semi"
                      onClick={() => {
                        setActiveStep(activeStep - 1);
                        setOtp(new Array(6).fill(''));
                        resetTimer();
                      }}
                    >
                      Change
                    </button>
                  </p>
                  <div className="input-container">
                    <div className="flex-column h-auto w-full items-end justify-end">
                      <OtpInput setOtp={setOtp} otp={otp} />
                      {showOtpError ? (
                        <p className="field-wrapper-error-message">
                          Invalid OTP
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="action-container">
                    <button
                      type="submit"
                      id="login-continue-otp"
                      className="custom__button custom__button--dark text--bold w-100 stepperNext"
                      onClick={verifyOtp.isLoading ? () => {} : handleOtpSubmit}
                    >
                      {/* TODO : GTM */}
                      Verify
                    </button>
                  </div>
                  <p className="text--black text--semi login__existing text-center">
                    {seconds !== 0 || minutes !== 0 ? (
                      <>
                        <span className="text--gray">Resend OTP in </span>
                        {minutes < 10 ? `0${minutes}` : minutes}:
                        {seconds < 10 ? `0${seconds}` : seconds}
                      </>
                    ) : (
                      <button
                        type="button"
                        className="text--darkBlue text--semi cursor-pointer text-center"
                        onClick={resendOtp}
                      >
                        Resend
                      </button>
                    )}
                  </p>
                </form>
              )}
              {activeStep === 3 ? (
                <UserDetails
                  closeModal={() => {
                    closeModal();
                    navigateToNext();
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Auth;
